import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ContinuePage from "./pages/ContinuePage";
import BookingScreen from "./components/BookingScreen";
import SupportSessionBanner from './components/SupportSessionBanner';
import TermsPage from "./pages/TermsPage";
import ProposalPage from "./pages/ProposalPage";
import PartnerDashboard from "./pages/partner/Dashboard";
import PartnerClients from "./pages/partner/Clients";
import PartnerCredits from "./pages/partner/Credits";
import PartnerBranding from "./pages/partner/Branding";
import PartnerEarnings from "./pages/partner/Earnings";
import PartnerLeads from "./pages/partner/Leads";
import PartnerInvite from "./pages/partner/Invite";
import PartnerProfile from "./pages/partner/Profile";
import PartnerAgreement from "./pages/partner/Agreement";
import PartnerPromoCodes from "./pages/partner/PromoCodes";
import PartnerMarketplace from "./pages/partner/Marketplace";
import PartnerMarketplaceProject from "./pages/partner/MarketplaceProject";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminPartners from "./pages/admin/Partners";
import AdminClients from "./pages/admin/Clients";
import AdminCommissions from "./pages/admin/Commissions";
import AdminPayoutRequests from "./pages/admin/PayoutRequests";
import AdminRateCard from "./pages/admin/RateCard";
import AdminApprovals from "./pages/admin/Approvals";
import AdminWebhooks from "./pages/admin/Webhooks";
import AdminLeads from "./pages/admin/Leads";
import AdminPromoCodes from "./pages/admin/PromoCodes";
import AdminMarketplace from "./pages/admin/Marketplace";
import AdminMarketplaceProject from "./pages/admin/MarketplaceProject";
import NotificationsPage from "./pages/Notifications";
import "@/App.css";

// Protected Route wrapper
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ef785b]"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }
  
  return children;
};

// Public Route wrapper (redirects if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ef785b]"></div>
      </div>
    );
  }
  
  if (user) {
    // An applicant who has not finished applying belongs on the booking screen, not the
    // dashboard. Submitting signs them in, so this redirect fires the instant the session
    // lands — racing it with an imperative navigate() from RegisterPage is how the booking
    // screen ended up unreachable. Both paths agree on the destination instead.
    //
    // It also does the right thing later: someone who applied, closed the tab and came
    // back to /login gets returned to the booking they never made.
    if (user.role !== "admin" && user.application_complete === false) {
      return <Navigate to="/booked" replace />;
    }
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes. There is no landing page here — the partner marketing
          page lives at expedify.ai/partners, so the root is the sign-in page. */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
      {/* Not wrapped in PublicRoute. Submitting the application signs the applicant in, so
          they are already authenticated when the emailed link arrives, and PublicRoute
          would bounce them to a dashboard instead of the questions it asked them for. */}
      <Route path="/continue" element={<ContinuePage />} />
      {/* Book the call. A route rather than a piece of RegisterPage's state, for two
          reasons that both bit during QA:
            1. Submitting signs the applicant in, and PublicRoute redirects an authenticated
               user off /register instantly — so a booking screen rendered there is never
               seen by anybody.
            2. State does not survive a refresh. This is the one screen in the flow we most
               need to be able to get back to.
          Authenticated, because you only reach it by having just applied. */}
      <Route path="/booked" element={<ProtectedRoute><BookingScreen /></ProtectedRoute>} />
      {/* Not wrapped in PublicRoute: a signed-in partner must be able to read what they
          agreed to, and PublicRoute would bounce them to the dashboard. */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/proposal/:token" element={<ProposalPage />} />

      {/* Partner routes */}
      <Route path="/dashboard" element={<ProtectedRoute requiredRole="partner"><PartnerDashboard /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute requiredRole="partner"><PartnerClients /></ProtectedRoute>} />
      <Route path="/credits" element={<ProtectedRoute requiredRole="partner"><PartnerCredits /></ProtectedRoute>} />
      <Route path="/branding" element={<ProtectedRoute requiredRole="partner"><PartnerBranding /></ProtectedRoute>} />
      {/* Two paths, one page. Both are live links people already hold; Earnings opens the
          matching tab rather than 404ing or silently landing somewhere else. */}
      <Route path="/commissions" element={<ProtectedRoute requiredRole="partner"><PartnerEarnings /></ProtectedRoute>} />
      <Route path="/earnings" element={<ProtectedRoute requiredRole="partner"><PartnerEarnings /></ProtectedRoute>} />
      <Route path="/payouts" element={<ProtectedRoute requiredRole="partner"><PartnerEarnings /></ProtectedRoute>} />
      <Route path="/invite" element={<ProtectedRoute requiredRole="partner"><PartnerInvite /></ProtectedRoute>} />
      {/* Kept: bookmarks and older notification links point here. The nav says Invite. */}
      <Route path="/leads" element={<ProtectedRoute requiredRole="partner"><PartnerLeads /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute requiredRole="partner"><PartnerProfile /></ProtectedRoute>} />
      <Route path="/agreement" element={<ProtectedRoute requiredRole="partner"><PartnerAgreement /></ProtectedRoute>} />
      <Route path="/promo-codes" element={<ProtectedRoute requiredRole="partner"><PartnerPromoCodes /></ProtectedRoute>} />
      <Route path="/marketplace" element={<ProtectedRoute requiredRole="partner"><PartnerMarketplace /></ProtectedRoute>} />
      <Route path="/marketplace/:id" element={<ProtectedRoute requiredRole="partner"><PartnerMarketplaceProject /></ProtectedRoute>} />

      {/* Admin routes */}
      <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/partners" element={<ProtectedRoute requiredRole="admin"><AdminPartners /></ProtectedRoute>} />
      <Route path="/admin/clients" element={<ProtectedRoute requiredRole="admin"><AdminClients /></ProtectedRoute>} />
      <Route path="/admin/commissions" element={<ProtectedRoute requiredRole="admin"><AdminCommissions /></ProtectedRoute>} />
      <Route path="/admin/payout-requests" element={<ProtectedRoute requiredRole="admin"><AdminPayoutRequests /></ProtectedRoute>} />
      <Route path="/admin/rates" element={<ProtectedRoute requiredRole="admin"><AdminRateCard /></ProtectedRoute>} />
      <Route path="/admin/promo-codes" element={<ProtectedRoute requiredRole="admin"><AdminPromoCodes /></ProtectedRoute>} />
      <Route path="/admin/leads" element={<ProtectedRoute requiredRole="admin"><AdminLeads /></ProtectedRoute>} />
      <Route path="/admin/approvals" element={<ProtectedRoute requiredRole="admin"><AdminApprovals /></ProtectedRoute>} />
      <Route path="/admin/webhooks" element={<ProtectedRoute requiredRole="admin"><AdminWebhooks /></ProtectedRoute>} />
      <Route path="/admin/marketplace" element={<ProtectedRoute requiredRole="admin"><AdminMarketplace /></ProtectedRoute>} />
      <Route path="/admin/marketplace/:id" element={<ProtectedRoute requiredRole="admin"><AdminMarketplaceProject /></ProtectedRoute>} />
      
      {/* Shared (any authenticated user) */}
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          {/* Above the router on purpose: a support session must be visible on every screen,
              including the ones an admin never normally sees. */}
          <SupportSessionBanner />
          <AppRoutes />
        </NotificationProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              borderLeft: '4px solid #ee785b',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
